import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  styled,
  Link as MuiLink,
  Breadcrumbs,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHeart,
  faHeartBroken,
  faCalendarPlus,
  faArrowLeft,
  faCircleCheck,
  faCircleExclamation,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faBriefcase,
  faGraduationCap,
  faCalendarAlt,
  faClock,
  faLanguage,
  faBrain,
  faChartLine,
  faHandshake,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { SectionsHeading } from '../../../components/headings'
import Api from '../../../services/api.service'
import { handlingErrors } from '../../../utils'
import { Slide, toast, ToastContainer } from 'react-toastify'
import lightTheme from '../../../styles/theme/lightTheme'
import {
  disponibilitesList2,
  disponibleSousList2,
  niveauCompetencesList,
  niveauLanguesList,
} from '../../../data'
import moment from 'moment-timezone'
import { truncate } from '../../../config'
import AppointmentModal from '../../../components/modals/AppointmentModal'

const DetailContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '30px',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: '30px',
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    marginBottom: '24px',
  },
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
}))

const TitleBox = styled(Typography)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 14,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  textTransform: 'uppercase',
  [theme.breakpoints.down('sm')]: {
    fontSize: 12,
  },
}))

const ItemLong = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '20px',
  marginBottom: '20px',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}))

const ActionButton = styled(Button)(({ theme }) => ({
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'center',
  },
}))

const CandidateDetail = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const currentUser = useSelector((state) => state.userAuth.user)

  const [candidate, setCandidate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingFavorie, setLoadingFavorie] = useState(false)
  const [isFavorie, setIsFavorie] = useState(false)
  const [idFavori, setIdFavori] = useState(null)
  const [showAppointmentModal, setShowAppointmentModal] = useState(false)
  const [seeMore, setSeeMore] = useState(false)

  useEffect(() => {
    if (id) {
      fetchCandidateDetail()
    }
  }, [id])

  useEffect(() => {
    if (candidate) {
      checkFavorie()
    }
  }, [candidate])

  const fetchCandidateDetail = async () => {
    setLoading(true)
    try {
      const result = await Api.user.detail(id)
      const res = handlingErrors(result)

      if (res.ok) {
        setCandidate(res.data)
      } else if (res.status === 401) {
        toast.error(t('successMessage.sessionExpired'))
        navigate('/auth/se-connecter')
      } else {
        toast.error(t('successMessage.somethingWrong'))
      }
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'))
    }
    setLoading(false)
  }

  const checkFavorie = async () => {
    setLoadingFavorie(true)
    try {
      const result = await Api.favoris.listWithFilter({
        utilisateur: currentUser.data.id,
        favori: candidate.id,
      })
      const res = handlingErrors(result)

      if (res.ok && res.data.length > 0) {
        setIsFavorie(true)
        setIdFavori(res.data[0].id)
      }
      setLoadingFavorie(false)
    } catch (error) {
      console.error('Error checking favorite status:', error)
      setLoadingFavorie(false)
    }
  }

  const addToFavorie = async () => {
    setLoadingFavorie(true)
    const data = {
      utilisateur: '/api/utilisateurs/' + currentUser.data.id,
      favori: '/api/utilisateurs/' + candidate.id,
    }

    try {
      const result = await Api.favoris.add(data)
      const res = handlingErrors(result)

      if (res.ok) {
        setIsFavorie(true)
        setIdFavori(res.data.id)
        toast.success(t('successMessage.candidateAddedToFavorite'))
      }
      setLoadingFavorie(false)
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'))
      setLoadingFavorie(false)
    }
  }

  const removeFromFavorie = async () => {
    setLoadingFavorie(true)
    try {
      const result = await Api.favoris.delete(idFavori)
      const res = handlingErrors(result)

      if (res.ok) {
        setIsFavorie(false)
        setIdFavori(null)
        toast.success(t('successMessage.candidateRemovedFromFavorite'))
      }
      setLoadingFavorie(false)
    } catch (error) {
      toast.error(t('successMessage.somethingWrong'))
      setLoadingFavorie(false)
    }
  }

  const handleSeeMore = () => {
    setSeeMore(!seeMore)
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, md: 0 } }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (!candidate) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, md: 0 } }}>
        <Typography variant="h6" align="center">
          {t('profile.candidateNotFound')}
        </Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, md: 0 } }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 4 }} separator="›">
        <MuiLink
          component={Link}
          to="/"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {t('button.home')}
        </MuiLink>
        <MuiLink component={Link} to="/user/candidats" underline="hover">
          {t('profile.candidatListTitle')}
        </MuiLink>
        <Typography color="text.primary">
          {i18n.language === 'fr'
            ? candidate.candidat.poste.titre
            : candidate.candidat.poste.titreEn}{' '}
          (#Jober000{candidate.id})
        </Typography>
      </Breadcrumbs>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <SectionsHeading type="animation">
          {t('profile.candidateDetail')}
        </SectionsHeading>
        {/*   <br></br>
        <Typography variant="body" sx={{ fontSize: { xs: 12, md: 18 } }}>
          #Jober000{candidate.id}
        </Typography> */}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <ActionButton
          variant="contained"
          onClick={isFavorie ? removeFromFavorie : addToFavorie}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            background: isFavorie
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#FFFFFF',
          }}
        >
          {loadingFavorie ? (
            <CircularProgress size={16} color="inherit" sx={{ marginRight: '8px' }} />
          ) : (
            <FontAwesomeIcon
              icon={isFavorie ? faHeartBroken : faHeart}
              style={{ marginRight: '8px' }}
            />
          )}
          {isFavorie
            ? t('profile.removeFromFavorites')
            : t('profile.addToFavorites')}
        </ActionButton>

        <ActionButton
          variant="outlined"
          onClick={() => setShowAppointmentModal(true)}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            borderColor: lightTheme.palette.primary.main,
            color: lightTheme.palette.primary.main,
          }}
        >
          <FontAwesomeIcon
            icon={faCalendarPlus}
            style={{ marginRight: '8px' }}
          />
          {t('profile.scheduleAppointment', 'Proposer un rendez-vous')}
        </ActionButton>
      </Box>

      {/* Candidate Information */}
      <DetailContainer>
        {/* Header Section */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 2, md: 3 },
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.7rem', md: '2.125rem' } }}>
              {candidate.prenom} {candidate.nom}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faEnvelope} fontSize={14} />
                <Typography variant="body2" color="white">
                  {candidate.email}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faPhone} fontSize={14} />
                <Typography variant="body2" color="white">
                  {candidate.telephone}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} fontSize={14} />
                <Typography variant="body2" color="white">
                  {candidate.ville} / {candidate.pays}
                </Typography>
              </Box>
            </Box>
            {candidate.candidat?.poste && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FontAwesomeIcon icon={faBriefcase} fontSize={14} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600 }}
                  color="white"
                >
                  {i18n.language === 'fr'
                    ? candidate.candidat.poste.titre
                    : candidate.candidat.poste.titreEn}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Resume */}
          {candidate.candidat?.resume && (
            <Grid item xs={12}>
              <ItemLong>
                <TitleContainer>
                  <TitleBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon
                        icon={faGraduationCap}
                        color="#667eea"
                        fontSize={16}
                      />
                      {t('profile.candidat.descriptif')}
                    </Box>
                  </TitleBox>
                </TitleContainer>
                <Box
                  sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: '8px' }}
                >
                  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
                    {seeMore
                      ? candidate.candidat.resume
                      : truncate(candidate.candidat.resume)}
                  </Typography>
                  {candidate.candidat.resume.length > 150 && (
                    <Button
                      onClick={handleSeeMore}
                      sx={{
                        textTransform: 'none',
                        p: 0,
                        color: '#667eea',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        },
                      }}
                    >
                      {seeMore ? t('button.seeLess') : t('button.seeMore')}
                    </Button>
                  )}
                </Box>
              </ItemLong>
            </Grid>
          )}

          {/* Disponibilité */}
          {candidate.candidat.typeDisponibilite && (
            <Grid item xs={12} md={6}>
              <ItemLong>
                <TitleContainer>
                  <TitleBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        color="#667eea"
                        fontSize={16}
                      />
                      {t('profile.candidat.disponibilite')}
                    </Box>
                  </TitleBox>
                </TitleContainer>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #bae6fd',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#0c4a6e' }}>
                    {
                      disponibilitesList2.find(
                        (dispo) =>
                          dispo.id === candidate.candidat.typeDisponibilite
                      )?.titre[i18n.language]
                    }
                    {candidate.candidat.typeDisponibilite === 2 &&
                      candidate.candidat.disponibleSous &&
                      ` ${
                        disponibleSousList2.find(
                          (dispoS) =>
                            dispoS.id === candidate.candidat.disponibleSous
                        )?.titre[i18n.language]
                      }`}
                    {candidate.candidat.typeDisponibilite === 4 &&
                      ` ${moment(candidate.candidat.disponible)
                        .tz('Europe/Paris')
                        .format('DD/MM/YYYY')}`}
                  </Typography>
                </Box>
              </ItemLong>
            </Grid>
          )}

          {/* Temps de travail */}
          <Grid item xs={12} md={6}>
            <ItemLong>
              <TitleContainer>
                <TitleBox>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontAwesomeIcon
                      icon={faClock}
                      color="#667eea"
                      fontSize={16}
                    />
                    {t('box.timeWorkType')}
                  </Box>
                </TitleBox>
              </TitleContainer>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  border: '1px solid #fbbf24',
                }}
              >
                <Typography variant="body2" sx={{ color: '#92400e' }}>
                  {candidate.candidat.typeTempsTravail === 'partiel'
                    ? `${t('box.timeWorkType2')} (${
                        candidate.candidat.tempPartiel
                      }%)`
                    : t('box.timeWorkType1')}
                </Typography>
              </Box>
            </ItemLong>
          </Grid>

          {/* Langues */}
          {((candidate.niveau_langue &&
            JSON.parse(candidate.niveau_langue).length > 0 &&
            JSON.parse(candidate.niveau_langue)[0].langue !== null) ||
            (candidate.langues && candidate.langues.length > 0)) && (
            <Grid item xs={12}>
              <ItemLong>
                <TitleContainer>
                  <TitleBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon
                        icon={faLanguage}
                        color="#667eea"
                        fontSize={16}
                      />
                      {t('filter.languages')}
                    </Box>
                  </TitleBox>
                </TitleContainer>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {candidate.userLangues
                    ? candidate.userLangues.map((langue, i) => (
                        <Box
                          key={i}
                          sx={{
                            px: 2,
                            py: 1,
                            backgroundColor: '#e0f2fe',
                            borderRadius: '20px',
                            border: '1px solid #0284c7',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: '#0c4a6e', fontWeight: 500 }}
                          >
                            {langue.langue} (
                            {
                              niveauLanguesList[langue.niveau - 1]?.titre[
                                i18n.language
                              ]
                            }
                            )
                          </Typography>
                        </Box>
                      ))
                    : candidate.langues.map((langue, i) => (
                        <Box
                          key={i}
                          sx={{
                            px: 2,
                            py: 1,
                            backgroundColor: '#e0f2fe',
                            borderRadius: '20px',
                            border: '1px solid #0284c7',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: '#0c4a6e', fontWeight: 500 }}
                          >
                            {langue}
                          </Typography>
                        </Box>
                      ))}
                </Box>
              </ItemLong>
            </Grid>
          )}

          {/* Compétences */}
          {candidate.candidat?.competences && (
            <Grid item xs={12}>
              <ItemLong>
                <TitleContainer>
                  <TitleBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon
                        icon={faBrain}
                        color="#667eea"
                        fontSize={16}
                      />
                      {t('filter.competences')}
                    </Box>
                  </TitleBox>
                </TitleContainer>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {candidate.candidat.competences.map((competence, i) => {
                    const level =
                      niveauCompetencesList[competence.niveau - 1]?.titre[
                        i18n.language
                      ]
                    const levelColor =
                      competence.niveau >= 4
                        ? '#10b981'
                        : competence.niveau >= 2
                        ? '#f59e0b'
                        : '#ef4444'
                    return (
                      <Box
                        key={i}
                        sx={{
                          px: 2,
                          py: 1,
                          backgroundColor: '#f3f4f6',
                          borderRadius: '8px',
                          border: `1px solid ${levelColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {competence.competence}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: levelColor, fontWeight: 600 }}
                        >
                          {level}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              </ItemLong>
            </Grid>
          )}

          {/* Compétences Annexes */}
          {candidate.candidat?.competencesAnnexes && (
            <Grid item xs={12}>
              <ItemLong>
                <TitleContainer>
                  <TitleBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon
                        icon={faChartLine}
                        color="#667eea"
                        fontSize={16}
                      />
                      {t('filter.competencesAnnexes')}
                    </Box>
                  </TitleBox>
                </TitleContainer>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {candidate.candidat.competencesAnnexes.map(
                    (competence, i) => {
                      const level =
                        niveauCompetencesList[competence.niveau - 1]?.titre[
                          i18n.language
                        ]
                      const levelColor =
                        competence.niveau >= 4
                          ? '#10b981'
                          : competence.niveau >= 2
                          ? '#f59e0b'
                          : '#ef4444'
                      return (
                        <Box
                          key={i}
                          sx={{
                            px: 2,
                            py: 1,
                            backgroundColor: '#fef3c7',
                            borderRadius: '8px',
                            border: `1px solid ${levelColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {competence.competence}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: levelColor, fontWeight: 600 }}
                          >
                            {level}
                          </Typography>
                        </Box>
                      )
                    }
                  )}
                </Box>
              </ItemLong>
            </Grid>
          )}

          {/* Softskils */}
          {candidate.candidat?.softskils && (
            <Grid item xs={12}>
              <ItemLong>
                <TitleContainer>
                  <TitleBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon
                        icon={faHandshake}
                        color="#667eea"
                        fontSize={16}
                      />
                      {t('filter.softskils')}
                    </Box>
                  </TitleBox>
                </TitleContainer>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {candidate.candidat.softskils.map((softskil, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor:
                          'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                        borderRadius: '20px',
                        border: '1px solid #0ea5e9',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#0c4a6e', fontWeight: 500 }}
                      >
                        {softskil}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </ItemLong>
            </Grid>
          )}

          {/* Valeurs */}
          {candidate.candidat?.valeurs && (
            <Grid item xs={12}>
              <ItemLong>
                <TitleContainer>
                  <TitleBox>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon
                        icon={faHeart}
                        color="#667eea"
                        fontSize={16}
                      />
                      {t('filter.values')}
                    </Box>
                  </TitleBox>
                </TitleContainer>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {candidate.candidat.valeurs.map((valeur, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor:
                          'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                        borderRadius: '20px',
                        border: '1px solid #ef4444',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#991b1b', fontWeight: 500 }}
                      >
                        {valeur}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </ItemLong>
            </Grid>
          )}
        </Grid>
      </DetailContainer>

      {/* Appointment Modal */}
      <AppointmentModal
        open={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        candidate={candidate}
        onSuccess={() => {
          // Optional: refresh data or show success message
        }}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        transition={Slide}
        className={'toast'}
      />
    </Container>
  )
}

export default CandidateDetail
